// src/utils/dateHelpers.ts
//
// Locale-aware date formatting shared across the app. Georgian ("ka") renders
// month names in Georgian; English ("en") falls back to the en-GB short month.
//
// Prefer passing the reactive `language` from useTranslation()/useLanguage() so
// dates update the instant the on-page language switcher is toggled. Call sites
// that can't reach the context (PDF builders, plain modules) may omit it and
// fall back to getActiveLanguage().

export type AppLanguage = "en" | "ka";

const GEORGIAN_MONTHS_ABBR = [
  "იან", "თებ", "მარ", "აპრ", "მაი", "ივნ",
  "ივლ", "აგვ", "სექ", "ოქტ", "ნოე", "დეკ",
];

export function getGeorgianMonthAbbreviation(monthIndex: number): string {
  return GEORGIAN_MONTHS_ABBR[monthIndex];
}

// Reads the active UI language the same way LanguageContext persists it
// (localStorage key "language"). Used as the default when a caller can't pass
// the reactive value from context.
export function getActiveLanguage(): AppLanguage {
  return typeof localStorage !== "undefined" && localStorage.getItem("language") === "ka"
    ? "ka"
    : "en";
}

// Normalizes the date shapes used across the app — Firestore Timestamp, Date,
// ISO string, epoch ms, or a { seconds } object — into a Date. Returns null for
// empty or unparseable input.
export function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDateGeorgian(date: Date): string {
  const day = date.getDate().toString().padStart(2, "0");
  const month = getGeorgianMonthAbbreviation(date.getMonth());
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

// Locale-aware short date, e.g. "16 ივნ 2026" (ka) / "16 Jun 2026" (en).
// Accepts any date shape via toDate(); returns "—" for empty/invalid input.
export function formatDate(
  value: any,
  language: AppLanguage = getActiveLanguage()
): string {
  const d = toDate(value);
  if (!d) return "—";
  if (language === "ka") return formatDateGeorgian(d);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
